import * as React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import AutoFixHighRoundedIcon from "@mui/icons-material/AutoFixHighRounded";
import ConstructionRoundedIcon from "@mui/icons-material/ConstructionRounded";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import SettingsSuggestRoundedIcon from "@mui/icons-material/SettingsSuggestRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import ThumbUpAltRoundedIcon from "@mui/icons-material/ThumbUpAltRounded";
import { useTranslation } from "react-i18next";

export default function Highlights() {
  const { t } = useTranslation();
  const theme = useTheme();

  const items = [
    {
      icon: <SettingsSuggestRoundedIcon />,
      title: t("landingpage.highlightstitle1"),
      description: t("landingpage.highlightstext1"),
    },
    {
      icon: <ConstructionRoundedIcon />,
      title: t("landingpage.highlightstitle2"),
      description: t("landingpage.highlightstext2"),
    },
    {
      icon: <ThumbUpAltRoundedIcon />,
      title: t("landingpage.highlightstitle3"),
      description: t("landingpage.highlightstext3"),
    },
    {
      icon: <AutoFixHighRoundedIcon />,
      title: t("landingpage.highlightstitle4"),
      description: t("landingpage.highlightstext4"),
    },
    {
      icon: <SupportAgentRoundedIcon />,
      title: t("landingpage.highlightstitle5"),
      description: t("landingpage.highlightstext5"),
    },
    {
      icon: <QueryStatsRoundedIcon />,
      title: t("landingpage.highlightstitle6"),
      description: t("landingpage.highlightstext6"),
    },
  ];

  return (
    <Box
      id="highlights"
      sx={{
        pt: { xs: 4, sm: 12 },
        pb: { xs: 10, sm: 14 },
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)"
            : "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
      }}
    >
      <Container
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: { xs: 3, sm: 6 },
        }}
      >
        <Box
          sx={{
            width: { sm: "100%", md: "60%" },
            textAlign: { sm: "left", md: "center" },
          }}
        >
          <Typography
            component="h2"
            variant="h4"
            sx={{
              color: "text.primary",
              fontWeight: 600,
              mb: 2,
              textAlign: "center",
            }}
          >
            {t("landingpage.highlightsmaintitle")}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              fontSize: "1.1rem",
              lineHeight: 1.6,
              textAlign: "center",
            }}
          >
            {t("startpage.summarytext")}
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {items.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  p: 3,
                  height: "100%",
                  background:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(255, 255, 255, 0.8)",
                  backdropFilter: "blur(10px)",
                  border: `1px solid ${
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(0, 0, 0, 0.1)"
                  }`,
                  borderRadius: 2,
                  transition: "all 0.3s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow:
                      theme.palette.mode === "dark"
                        ? "0 8px 32px rgba(0, 0, 0, 0.3)"
                        : "0 8px 32px rgba(0, 0, 0, 0.1)",
                    background:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.08)"
                        : "rgba(255, 255, 255, 0.95)",
                  },
                }}
              >
                <Stack direction="column" spacing={2} sx={{ height: "100%" }}>
                  <Box
                    sx={{
                      color: "primary.main",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                    }}
                  >
                    {React.cloneElement(item.icon, {
                      sx: { fontSize: "2rem" },
                    })}
                  </Box>

                  <Box sx={{ flexGrow: 1 }}>
                    <Typography
                      variant="h6"
                      component="h3"
                      gutterBottom
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        mb: 1,
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        lineHeight: 1.6,
                      }}
                    >
                      {item.description}
                    </Typography>
                  </Box>
                </Stack>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
